import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Layout from '../components/Layout';
import Container from '../components/Container';
import Button from '../components/Button';
import Footer from '../components/Footer';

// Top Banner Component
const TopBanner = styled.div`
  width: 100%;
  background: linear-gradient(135deg, rgba(45, 108, 255, 0.9) 0%, rgba(110, 168, 255, 0.8) 100%);
  color: #fff;
  padding: 0.8rem 2rem;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(45, 108, 255, 0.3);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    padding: 0.7rem 1rem;
    font-size: 0.9rem;
  }
`;

const BannerLink = styled.a`
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  transition: all 0.2s ease;
  
  &:hover {
    text-decoration: underline;
    color: #f0f7ff;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: 
    radial-gradient(circle at 20% 50%, rgba(110, 168, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(45, 108, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(58, 143, 255, 0.08) 0%, transparent 50%),
    linear-gradient(135deg, #0a0f2e 0%, #1a2451 50%, #0e1633 100%);
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.8s ease-out;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  background-image: 
    linear-gradient(45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(-45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #6ea8ff 75%),
    linear-gradient(-45deg, transparent 75%, #6ea8ff 75%);
  background-size: 60px 60px;
  background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
  pointer-events: none;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  padding: 3rem 2rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 2rem;
  color: transparent;
  background: linear-gradient(135deg, #6ea8ff 0%, #ffffff 50%, #3a8fff 100%);
  background-clip: text;
  -webkit-background-clip: text;
  font-weight: 900;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
`;

const FeaturesSection = styled.section`
  max-width: 900px;
  margin: 0 auto 3rem auto;
  padding: 2.5rem 2rem;
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
  border-radius: 24px;
  border: 1px solid rgba(110, 168, 255, 0.2);
  backdrop-filter: blur(20px);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  text-align: center;
  color: #fff;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const FeaturesDescription = styled.p`
  color: #b6d0ff;
  font-size: 1.15rem;
  margin: 0 auto 2rem auto;
  max-width: 700px;
  line-height: 1.7;
  opacity: 0.9;
`;



const PricingCardsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  @media (max-width: 900px) {
    gap: 1.2rem;
  }
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }
`;

const GlassCard = styled.div`
  position: relative;
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
  color: #fff;
  border-radius: 24px;
  border: 1px solid rgba(110, 168, 255, 0.2);
  backdrop-filter: blur(20px);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  min-width: 280px;
  max-width: 340px;
  width: 100%;
  padding: 3.2rem 2.2rem 2.5rem 2.2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  z-index: 1;
  margin-top: 2.2rem;
  
  @media (max-width: 700px) {
    min-width: 0;
    max-width: 400px;
    padding: 2.2rem 1.2rem 1.5rem 1.2rem;
    margin-top: 2.7rem;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 30px 60px rgba(0, 0, 0, 0.4),
      0 0 40px rgba(110, 168, 255, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    border-color: rgba(110, 168, 255, 0.5);
  }
  
  ${props => props.$plan === 'quarterly' && `
    background: 
      linear-gradient(145deg, rgba(110, 168, 255, 0.2) 0%, rgba(58, 143, 255, 0.1) 100%),
      linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
    border-color: rgba(110, 168, 255, 0.4);
  `}
  
  ${props => props.$plan === 'lifetime' && `
    background: 
      linear-gradient(145deg, rgba(255, 107, 107, 0.2) 0%, rgba(255, 142, 142, 0.1) 100%),
      linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
    border-color: rgba(255, 107, 107, 0.4);
    transform: scale(1.08);
    z-index: 2;
    margin-top: 0;
    min-width: 300px;
    max-width: 360px;
    
    @media (max-width: 700px) {
      transform: scale(1.05);
      min-width: 0;
      max-width: 420px;
    }
  `}
`;

const CardBadge = styled.div`
  position: absolute;
  top: -18px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%);
  color: #fff;
  padding: 0.7rem 1.6rem;
  border-radius: 16px;
  font-size: 1.08rem;
  font-weight: 800;
  box-shadow: 0 4px 16px rgba(255, 107, 107, 0.3);
  letter-spacing: 0.01em;
  z-index: 2;
  text-align: center;
  backdrop-filter: blur(10px);
`;

const CardTitle = styled.h3`
  font-size: 1.45rem;
  font-weight: 800;
  margin-bottom: 1.2rem;
  color: #fff;
  text-align: center;
`;

const CardPrice = styled.div`
  font-size: 3.1rem;
  font-weight: bold;
  margin-bottom: 0.2rem;
  color: #fff;
  text-align: center;
`;

const CardPeriod = styled.span`
  font-size: 1.15rem;
  font-weight: 500;
  opacity: 0.7;
  color: #b6d0ff;
  margin-left: 0.3rem;
`;

const CardButton = styled(Button)`
  margin-top: 2.2rem;
  width: 100%;
  font-size: 1.18rem;
  border-radius: 12px;
  font-weight: 700;
  border: none;
  padding: 1.1rem 0;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  background: linear-gradient(145deg, rgba(110, 168, 255, 0.3) 0%, rgba(58, 143, 255, 0.4) 100%);
  color: #fff;
  font-family: inherit;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(110, 168, 255, 0.3);
  
  &:hover {
    background: linear-gradient(145deg, rgba(110, 168, 255, 0.4) 0%, rgba(58, 143, 255, 0.5) 100%);
    transform: translateY(-2px) scale(1.02);
    border-color: rgba(110, 168, 255, 0.5);
    box-shadow: 0 8px 24px rgba(110, 168, 255, 0.3);
  }
  
  ${props => props.$plan === 'lifetime' && `
    background: linear-gradient(145deg, rgba(255, 107, 107, 0.3) 0%, rgba(255, 142, 142, 0.4) 100%);
    border-color: rgba(255, 107, 107, 0.3);
    
    &:hover {
      background: linear-gradient(145deg, rgba(255, 107, 107, 0.4) 0%, rgba(255, 142, 142, 0.5) 100%);
      border-color: rgba(255, 107, 107, 0.5);
      box-shadow: 0 8px 24px rgba(255, 107, 107, 0.3);
    }
  `}
`;

const CardSavings = styled.div`
  color: #b6d0ff;
  font-size: 1.08rem;
  font-weight: 500;
  margin-top: 1.7rem;
  text-align: center;
  opacity: 0.8;
`;

const SupportInfo = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding: 1rem;
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.6) 0%, rgba(20, 30, 60, 0.8) 100%);
  border-radius: 16px;
  color: #b6d0ff;
  border: 1px solid rgba(110, 168, 255, 0.1);
  backdrop-filter: blur(20px);
  
  @media (max-width: 768px) {
    margin-top: 1.5rem;
    padding: 0.8rem;
  }
`;

const SupportText = styled.p`
  font-size: 1rem;
  color: #b6d0ff;
  opacity: 0.9;
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const SupportLink = styled.a`
  font-weight: bold;
  color: #6ea8ff;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    color: #3a8fff;
  }
`;

const CryptoOptions = styled.div`
  margin-top: 2rem;
  margin-bottom: 2rem;
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
  border-radius: 24px;
  padding: 1.5rem;
  border: 1px solid rgba(110, 168, 255, 0.2);
  backdrop-filter: blur(20px);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
    padding: 1.2rem;
  }
`;

const CryptoOptionsTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  text-align: center;
  color: #fff;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.8rem;
  }
`;

const CryptoSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.8rem;
    margin-top: 0.8rem;
  }
`;

const CryptoOption = styled.div`
  padding: 0.75rem;
  border: 2px solid ${props => props.selected ? '#6ea8ff' : 'rgba(110, 168, 255, 0.2)'};
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.selected ? 'rgba(110, 168, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  color: #b6d0ff;
  backdrop-filter: blur(10px);
  
  &:hover {
    border-color: #6ea8ff;
    transform: translateY(-2px);
    background: rgba(110, 168, 255, 0.15);
  }
  
  @media (max-width: 480px) {
    padding: 0.6rem;
  }
`;

const CryptoIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #6ea8ff;
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 0.3rem;
  }
`;

const CryptoName = styled.div`
  font-size: 0.8rem;
  font-weight: ${props => props.selected ? 'bold' : 'normal'};
  color: #b6d0ff;
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const PaymentSummary = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
  border-radius: 24px;
  border: 1px solid rgba(110, 168, 255, 0.2);
  backdrop-filter: blur(20px);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  color: #fff;
  
  @media (max-width: 768px) {
    margin-top: 1.5rem;
    padding: 1.2rem;
  }
  
  h3 {
    color: #fff;
    @media (max-width: 768px) {
      font-size: 1.1rem;
      margin-bottom: 0.8rem;
    }
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(110, 168, 255, 0.2);
  color: #b6d0ff;
  &:last-child {
    border-bottom: none;
    font-weight: bold;
  }
  @media (max-width: 480px) {
    padding: 0.6rem 0;
    font-size: 0.95rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  gap: 1rem;
  
  button {
    min-width: 120px;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.8rem;
    
    button {
      width: 100%;
    }
  }
`;

const ErrorMessage = styled.div`
  background: 
    linear-gradient(145deg, rgba(255, 107, 107, 0.15) 0%, rgba(255, 107, 107, 0.1) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
  color: #ff6b6b;
  padding: 1rem;
  border-radius: 16px;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 500;
  border: 1px solid rgba(255, 107, 107, 0.3);
  backdrop-filter: blur(20px);
  
  @media (max-width: 768px) {
    padding: 0.8rem;
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
  }
`;

// Update the plans array with new pricing
const plans = [
  {
    id: 'test',
    name: 'Testing Plan',
    price: 5,
    features: [
      'Test plan for $5',
      'Limited features',
      'For development/testing only'
    ],
    featured: false
  },
  {
    id: 'monthly',
    name: 'Monthly',
    price: 60,
    features: [
      '1 month access to DeservedWarmer',
      'Full browser profile warming for Amazon, eBay, and Vinted',
      'Access to all automation features',
      'Natural browsing pattern simulation',
      'Cookie and session management',
      'Detailed activity logs',
      'AdsPower integration',
      'Standard customer support',
      'Software updates included'
    ],
    featured: false
  },
  {
    id: 'quarterly',
    name: 'Quarterly',
    price: 140,
    features: [
      '3 months access to DeservedWarmer',
      'Full browser profile warming for Amazon, eBay, and Vinted',
      'Access to all automation features',
      'Natural browsing pattern simulation',
      'Cookie and session management',
      'Detailed activity logs',
      'AdsPower integration',
      'Priority customer support',
      'Advanced profile management tools',
      'Regular software updates and improvements',
      'Save $50 compared to monthly'
    ],
    featured: false
  },
  {
    id: 'lifetime',
    name: 'Lifetime',
    price: 300,
    features: [
        'Lifetime access to DeservedWarmer',
      'Full browser profile warming for Amazon, eBay, and Vinted',
      'Access to all automation features',
      'Natural browsing pattern simulation',
      'Cookie and session management',
      'Detailed activity logs',
      'AdsPower integration',
      'Priority customer support',
      'Advanced profile management tools',
      'All future updates and improvements',
      'One-time payment, no recurring fees'
    ],
    featured: true
  }
];

// Available cryptocurrencies
const cryptoOptions = [
  { id: 'ltc', name: 'Litecoin', icon: 'Ł' },
  { id: 'btc', name: 'Bitcoin', icon: '₿' },
  { id: 'eth', name: 'Ethereum', icon: 'Ξ' },
  { id: 'usdttrc20', name: 'USDT (TRC-20)', icon: '₮', note: 'Low fees' },
  { id: 'usdterc20', name: 'USDT (ERC-20)', icon: '₮', note: 'Higher fees' }
];


function BuyPage() {
  const [selectedPlan, setSelectedPlan] = useState('quarterly');
  const [selectedCrypto, setSelectedCrypto] = useState('ltc');
  const [step, setStep] = useState(1); // 1: select plan, 2: select crypto
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentEstimate, setPaymentEstimate] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('pay') === '1') {
      setStep(2);
    }
    const planParam = params.get('plan');
    if (planParam && ['monthly', 'quarterly', 'lifetime', 'test'].includes(planParam)) {
      setSelectedPlan(planParam);
    }
  }, [location.search]);
  
  // Get the currently selected plan
  const currentPlan = plans.find(plan => plan.id === selectedPlan);
  
  // When user selects a new cryptocurrency, get an estimate
  useEffect(() => {
    if (step === 2 && selectedCrypto && currentPlan) {
      // Get real exchange rate estimate from NOWPayments
      const getPaymentEstimate = async () => {
        try {
          const response = await fetch('https://api.nowpayments.io/v1/estimate', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              amount: currentPlan.price,
              currency_from: 'usd',
              currency_to: selectedCrypto
            })
          });
          const data = await response.json();
          if (data.estimated_amount) {
            setPaymentEstimate({
              fiat: currentPlan.price,
              crypto: data.estimated_amount.toFixed(8),
              currency: selectedCrypto
            });
          }
        } catch (error) {
          console.error('Error getting estimate:', error);
          // Fallback to showing just USD if estimate fails
          setPaymentEstimate({
            fiat: currentPlan.price,
            crypto: '---',
            currency: selectedCrypto
          });
        }
      };
      getPaymentEstimate();
    }
  }, [step, selectedCrypto, selectedPlan, currentPlan]);
  
  const proceedToPayment = () => {
    setStep(2);
  };
  
  const handleCryptoSelect = (cryptoId) => {
    setSelectedCrypto(cryptoId);
  };
  
  const handlePurchase = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/payments/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: selectedPlan,
          crypto_currency: selectedCrypto
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        navigate(`/status/${data.payment.payment_id}`);
      } else {
        setError(data.message || 'Error creating payment. Please try again.');
      }
    } catch (error) {
      console.error('Payment creation error:', error);
      setError('Network error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Sort the plans array so that monthly is first, lifetime is in the middle, and quarterly is last.
  const sortedPlans = [
    plans.find(p => p.id === 'monthly'),
    plans.find(p => p.id === 'lifetime'),
    plans.find(p => p.id === 'quarterly')
  ].filter(Boolean);
  
  return (
      <Layout>
        <TopBanner>
          Join our channels: <BannerLink href="https://t.me/DeservedGatewayBot" target="_blank" rel="noopener noreferrer">@DeservedGatewayBot</BannerLink>
        </TopBanner>
        <PageWrapper>
          <BackgroundPattern />
          <ContentWrapper>
            <Container narrow>
              
              {error && (
                <ErrorMessage>{error}</ErrorMessage>
              )}
              
              {step === 1 ? (
                <>
                  <PageTitle>Choose Your Plan</PageTitle>
                  <FeaturesSection>
                    <FeaturesDescription>
                      Select the plan that works best for you. All plans include full access to DeservedWarmer with all features, 
                      platforms, and support. Upgrade or downgrade anytime.
                    </FeaturesDescription>
                  </FeaturesSection>
                  
                  <PricingCardsRow>
                    {sortedPlans.map((plan, index) => (
                      <GlassCard key={plan.id} $plan={plan.id}>
                        {plan.featured && <CardBadge>Most Popular</CardBadge>}
                        <CardTitle>{plan.name}</CardTitle>
                        <CardPrice>
                          ${plan.price}
                          <CardPeriod>USD</CardPeriod>
                        </CardPrice>
                        <CardButton
                          $plan={plan.id}
                          onClick={() => {
                            setSelectedPlan(plan.id);
                            proceedToPayment();
                          }}
                        >
                          {plan.id === 'quarterly' ? 'Best Value →' : plan.id === 'lifetime' ? 'Get Lifetime →' : 'Get Started →'}
                        </CardButton>
                        {plan.id === 'quarterly' && (
                          <CardSavings>Save $40 vs monthly billing</CardSavings>
                        )}
                        {plan.id === 'lifetime' && (
                          <CardSavings>One payment, lifetime access</CardSavings>
                        )}
                      </GlassCard>
                    ))}
                  </PricingCardsRow>
                  
                  <SupportInfo>
                    <SupportText>
                      Have questions? Join our community: <SupportLink href="https://t.me/DeservedGatewayBot" target="_blank" rel="noopener noreferrer">@DeservedGatewayBot</SupportLink>
                    </SupportText>
                  </SupportInfo>
                </>
              ) : (
                <>
                  <PageTitle>You're Almost There! 🚀</PageTitle>
                  <FeaturesSection>
                    <FeaturesDescription>
                      You've chosen the {currentPlan.name} plan. Complete your purchase below to get instant access to DeservedWarmer 
                      and start protecting your accounts from suspensions today.
                    </FeaturesDescription>
                  </FeaturesSection>
                  
                  <CryptoOptions>
                    <CryptoOptionsTitle>Choose Your Payment Method</CryptoOptionsTitle>
                    
                    {/* Main Cryptocurrencies Row */}
                    <CryptoSelector>
                      {cryptoOptions.filter(crypto => ['ltc', 'btc', 'eth'].includes(crypto.id)).map(crypto => (
                        <CryptoOption 
                          key={crypto.id}
                          selected={selectedCrypto === crypto.id}
                          onClick={() => handleCryptoSelect(crypto.id)}
                        >
                          <CryptoIcon>{crypto.icon}</CryptoIcon>
                          <CryptoName selected={selectedCrypto === crypto.id}>
                            {crypto.name}
                          </CryptoName>
                          {crypto.note && (
                            <div style={{ fontSize: '0.7rem', color: '#b6d0ff', marginTop: '0.3rem' }}>
                              {crypto.note}
                            </div>
                          )}
                        </CryptoOption>
                      ))}
                    </CryptoSelector>

                    {/* USDT Options Row - Centered */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      gap: '1rem', 
                      marginTop: '1rem',
                      flexWrap: 'wrap'
                    }}>
                      {cryptoOptions.filter(crypto => crypto.id.includes('usdt')).map(crypto => (
                        <CryptoOption 
                          key={crypto.id}
                          selected={selectedCrypto === crypto.id}
                          onClick={() => handleCryptoSelect(crypto.id)}
                          style={{ minWidth: '120px' }}
                        >
                          <CryptoIcon>{crypto.icon}</CryptoIcon>
                          <CryptoName selected={selectedCrypto === crypto.id}>
                            {crypto.name}
                          </CryptoName>
                          {crypto.note && (
                            <div style={{ fontSize: '0.7rem', color: '#b6d0ff', marginTop: '0.3rem' }}>
                              {crypto.note}
                            </div>
                          )}
                        </CryptoOption>
                      ))}
                    </div>
                    
                    <p style={{ textAlign: 'center', fontSize: '0.9rem', marginTop: '1rem', color: '#b6d0ff' }}>
                      Need different payment options? We also accept BNB, XRP, UK Bank Transfer, or PayPal. <br />
                      <SupportLink href="https://t.me/deserved0" target="_blank" rel="noopener noreferrer">
                        Contact @deserved0 on Telegram
                      </SupportLink> for alternative methods.
                    </p>
                  </CryptoOptions>
                  
                  {paymentEstimate && (
                    <PaymentSummary>
                      <h3>Order Summary</h3>
                      <SummaryRow>
                        <span>Plan:</span>
                        <span>{currentPlan.name} Access</span>
                      </SummaryRow>
                      <SummaryRow>
                        <span>Price (USD):</span>
                        <span>${paymentEstimate.fiat}</span>
                      </SummaryRow>
                      <SummaryRow>
                        <span>Payment Method:</span>
                        <span>{cryptoOptions.find(c => c.id === selectedCrypto).name}</span>
                      </SummaryRow>
                      <SummaryRow style={{ fontWeight: 'bold', color: '#10b981', fontSize: '1.1rem' }}>
                        <span>Access:</span>
                        <span>Instant after payment ✓</span>
                      </SummaryRow>
                    </PaymentSummary>
                  )}
                  
                  <SupportInfo>
                    <SupportText>
                      🔒 Secure crypto payments • ⚡ Instant access • 💬 Telegram support included
                    </SupportText>
                  </SupportInfo>
                  
                  <ButtonContainer>
                    <Button 
                      secondary
                      onClick={() => setStep(1)}
                      disabled={loading}
                    >
                      ← Back to Plans
                    </Button>
                    <Button 
                      primary
                      onClick={handlePurchase}
                      disabled={loading}
                      style={{ 
                        background: loading ? undefined : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        fontSize: '1.1rem',
                        padding: '1rem 2rem'
                      }}
                    >
                      {loading ? 'Creating Payment...' : `Pay $${currentPlan.price} →`}
                    </Button>
                  </ButtonContainer>
                </>
              )}
              
            </Container>
          </ContentWrapper>
          <Footer />
        </PageWrapper>
      </Layout>
  );
}

export default BuyPage; 